import { format } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';
import { ApiService } from 'src/app/services/api.service';
import { DataService } from 'src/app/services/data.service';
import { LoadingService } from 'src/app/services/loading.service';
import { NotificationService } from 'src/app/services/notification/notification.service';

import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Geolocation } from '@awesome-cordova-plugins/geolocation/ngx';
import { StorageService } from '@core/services';
import { AlertController, ModalController, PickerController, Platform } from '@ionic/angular';
import { ApiResponse } from '@shared/models';
import { TripStop } from '@shared/models/trips.model';

import { ScanSellTicketPage } from '../scan-sell-ticket/scan-sell-ticket.page';

@Component({
  selector: 'app-stops',
  templateUrl: './stops.page.html',
  styleUrls: ['./stops.page.scss'],
})
export class StopsPage implements OnInit {
  #notificationService: NotificationService = inject(NotificationService);
  realtrip;
  currentStopIndex: number;
  stops;
  realtrips: any[] = [];
  busLocationInterval;
  loading = false;

  constructor(
    private router: Router,
    private dataService: DataService,
    private apiService: ApiService,
    private platform: Platform,
    private alertCtrl: AlertController,
    private loadingService: LoadingService,
    private pickerCtrl: PickerController,
    private storage: StorageService,
    private geolocation: Geolocation,
    private modalCtrl: ModalController
  ) {}

  ngOnInit() {
    if (this.platform.is('cordova')) {
      // set to landscape
      // this.screenOrientation.lock(this.screenOrientation.ORIENTATIONS.LANDSCAPE);
    }
    this.getRealtrip();
  }

  ionViewDidEnter() {
    this.busLocationInterval = setInterval(() => {
      // Commented for now, as we are testing load average on backend
      // this.updateBusLocation();
    }, 10000);
  }

  ionViewDidLeave() {
    clearInterval(this.busLocationInterval);
  }

  updateBusLocation() {
    if (this.realtrip?.bus_id) {
      this.geolocation
        .getCurrentPosition()
        .then((resp) => {
          const lat = resp.coords.latitude + '';
          const long = resp.coords.longitude + '';
          if (this.realtrip && this.realtrip.bus_id) {
            this.apiService
              .postData('bus_location', {
                bus_id: this.realtrip.bus_id,
                lat,
                long,
              })
              .then((data) => {
                console.log('location: ', data);
              });
          }
        })
        .catch((error) => {
          console.log('Error getting location', error);
        });
    }
  }

  checkCurrentStopIndex() {
    if (this.realtrip && this.realtrip.current_stop_id && this.stops) {
      this.currentStopIndex = null;
      for (let index = 0; index < this.stops.length; index++) {
        if (this.stops[index].id === this.realtrip.current_stop_id) {
          this.currentStopIndex = index;
          break;
        }
      }
    } else {
      this.currentStopIndex = null;
    }
  }

  /**
   * Get the stops for the current trip
   */
  getTripStops() {
    this.stops = [];
    this.loading = true;
    this.apiService
      .getData('tripstops?id=' + this.realtrip.id)
      .then((res: ApiResponse<TripStop[]>) => {
        if (res.data) {
          this.stops = res.data.map((stop) => {
            const today = format(new Date(), 'yyyy-MM-dd');
            // const startsAtZonedAtMexico = zonedTimeToUtc(today + ' ' + stop.starts_at, 'America/Mexico_City');
            return {
              ...stop,
              starts_at: this.#formatZoneDate(today + ' ' + stop.starts_at),
            };
          });
        }
      })
      .finally(() => {
        this.loading = false;
        this.checkCurrentStopIndex();
      });
  }

  doRefresh(event) {
    this.getRealtrip();
    setTimeout(() => {
      event.target.complete();
    }, 1000);
  }

  getRealtrip() {
    if (!this.realtrip?.id) {
      this.getNextRealtrip();
      return;
    }
    this.loading = true;
    this.apiService
      .getData(`transportec_realtrips/${this.realtrip?.id}`)
      .then((res) => {
        console.log('realtrip! ', res);
        if (res?.data?.id) {
          this.realtrip = {
            ...res.data,
            title: res.data.route_name + ' - ' + format(new Date(res.data.starts_at), 'HH:mm'),
          };
          console.log('realtrip: ', this.realtrip);
          this.storage.set('realtrip', this.realtrip);
          this.getTripStops();
        }
      })
      .finally(() => {
        this.loading = false;
        this.checkCurrentStopIndex();
      });
  }

  getNextRealtrip() {
    this.loading = true;
    this.apiService
      .getData('transportec_realtrip')
      .then((res) => {
        console.log('realtrip! ', res);
        if (res?.realtrip?.id) {
          this.realtrip = {
            ...res.realtrip,
            title: res.realtrip.route_name + ' - ' + format(new Date(res.realtrip.starts_at), 'HH:mm'),
          };
          console.log('realtrip: ', this.realtrip);
          this.storage.set('realtrip', this.realtrip);
          this.getTripStops();
        }
      })
      .finally(() => {
        this.loading = false;
        this.checkCurrentStopIndex();
      });
  }

  async getRealtrips() {
    try {
      this.loadingService.present();
      const realtrips = await this.apiService.getData('transportec_realtrips');
      this.loadingService.dismiss();
      return realtrips.realtrips;
    } catch (error) {
      this.#notificationService.presentToast('Error al obtener los viajes. Intente de nuevo.');
      console.error('Error getting realtrips: ', error);
      setTimeout(() => {
        this.loadingService.dismiss();
      }, 1000);
      return [];
    }
  }

  async showRealtripsPicker() {
    this.realtrips = await this.getRealtrips();
    const myInputs = [];
    for (let j = 0; j < this.realtrips.length; j++) {
      myInputs.push({
        text: this.realtrips[j].route_name + ' - ' + this.#formatZoneDate(this.realtrips[j].starts_at),
        value: JSON.stringify(this.realtrips[j]),
      });
      if (j === this.realtrips.length - 1) {
        const picker = await this.pickerCtrl.create({
          columns: [
            {
              name: 'area',
              options: myInputs,
            },
          ],
          buttons: [
            {
              role: 'cancel',
              text: 'Cancelar',
            },
            {
              text: 'Seleccionar',
              handler: (data) => {
                const myData = JSON.parse(data.area.value);
                this.realtrip = {
                  ...myData,
                  title: myData.route_name + ' - ' + this.#formatZoneDate(myData.starts_at),
                };
                this.storage.set('realtrip', this.realtrip);
                this.getRealtrip();
                this.getTripStops();
              },
            },
          ],
        });
        picker.present();
      }
    }
  }

  updateTripStop(stop) {
    this.loadingService.present();
    this.apiService
      .putData('transportec_currentstop', {
        realtrip_id: this.realtrip.id,
        stop_id: stop.id,
      })
      .then((res) => {
        console.log('result updateTripStop: ', res);
        // this.getRealtrip();
        if (res && res.status) {
          this.getRealtrip();
        }
        if (res && res.stops) {
          // this.stops = res.stops;
          this.realtrip.current_stop_id = stop.id;
          this.checkCurrentStopIndex();
        }
      })
      .finally(() => {
        this.loadingService.dismiss();
      });
  }

  async endTrip() {
    const alert = await this.alertCtrl.create({
      header: 'Finalizar viaje',
      message: '¿Está seguro que desea finalizar el viaje?',
      buttons: [
        {
          text: 'No, cancelar',
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {
            console.log('Confirm Cancel');
          },
        },
        {
          text: 'Finalizar',
          handler: (res) => {
            console.log('Confirm Ok: ', res);
            this.endTripCall();
          },
        },
      ],
    });

    await alert.present();
  }

  startTrip() {
    this.loadingService.present();
    this.apiService
      .postData('transportec_starttrip', { realtrip_id: this.realtrip.id })
      .then((res) => {
        this.realtrip = res.data;
        this.getRealtrip();
        if (res.message) {
          this.#notificationService.presentToast(res.message);
        }
      })
      .finally(() => {
        this.loadingService.dismiss();
      });
  }

  endTripCall() {
    this.loadingService.present();
    this.apiService
      .putData('transportec_endtrip', { realtrip_id: this.realtrip.id })
      .then((res) => {
        console.log('trip ended: ', res);
        this.realtrip = { ...this.realtrip, active: false };
        if (res && res.status) {
          this.getRealtrip();
        }
      })
      .finally(() => {
        this.loadingService.dismiss();
      });
  }

  showUsers(stop) {
    this.dataService.setData(stop.id, stop);
    this.router.navigate(['/tecservice-driver/stops/' + stop.id]);
  }

  scan() {
    this.router.navigate(['/tecservice-driver/stops/scan']);
  }

  scanExit() {
    this.router.navigate(['/tecservice-driver/stops/scan-exit']);
  }

  async confirmShowQR() {
    const alert = await this.alertCtrl.create({
      header: 'Parada actual',
      message: this.stops[this.currentStopIndex || 0].name,
      buttons: [
        {
          text: 'No, cancelar',
          role: 'cancel',
          cssClass: 'secondary',
        },
        {
          text: 'Si',
          handler: (res) => {
            this.showQR();
          },
        },
      ],
    });

    await alert.present();
  }

  async showQR() {
    const qrModal = await this.modalCtrl.create({
      component: ScanSellTicketPage,
      componentProps: {
        realtrip: this.realtrip,
      },
    });
    await qrModal.present();
  }

  #formatZoneDate(date: string, stringFormat: string = 'HH:mm', zone: string = 'America/Mexico_City') {
    const startsAtZonedAtMexico = zonedTimeToUtc(date, zone);
    return format(startsAtZonedAtMexico, stringFormat);
  }
}
